/**
 * Pure candidate persistence/placement contract.
 *
 * COL-004E proved the 0.48-subcell placement shape. COL-007 productionizes
 * it through `src/sim/placement.ts` and the V1→V2 save migration.
 */

import { SLOTS, slotById, type SlotDef } from '../config/map';
import type { EntityKind, PlacedEntity } from '../types';
import {
  FOOTPRINT_CELL_SIZE,
  FOOTPRINT_DIVISION,
  ROTATION_QUARTERS,
  occupiedSetsOverlap,
  occupiedSubcells,
  subcellKey,
  visualFootprintRecommendation,
  type FootprintSpec,
  type RotationQuarter,
} from './footprints';
import {
  FOUNDATION_DIVISION,
  LEGACY_CELL_SIZE,
  createSubgridSpec,
  legacyGridToWorld,
  mapLegacyCenter,
  subcellWorldCenter,
  type SubgridCoord,
  type WorldPoint,
} from './subgrid';

export const PLACEMENT_DIVISION = FOUNDATION_DIVISION;
export const PLACEMENT_CELL_SIZE = LEGACY_CELL_SIZE / PLACEMENT_DIVISION;

const FOUNDATION_SPEC = createSubgridSpec(FOUNDATION_DIVISION);

export interface CandidatePlacement {
  /** Preserved current identity. Never derived solely from slotId. */
  id: string;
  kind: EntityKind;
  /** Current save slot when migrated; null for future-only candidates. */
  originSlotId: string | null;
  anchor: SubgridCoord;
  rotation: RotationQuarter;
  spec: FootprintSpec;
}

export type LegacyCompatibility =
  | { kind: 'legacy_representable'; slotId: string }
  | { kind: 'future_only'; reason: 'non_center_anchor' };

const SLOT_CENTER_BY_KEY = new Map<string, string>(
  SLOTS.map((slot) => [subcellKey(legacySlotCenterAnchor(slot)), slot.id]),
);

function assertNonEmptyId(id: string): void {
  if (typeof id !== 'string' || id.length === 0) {
    throw new Error('Candidate placement id must be a non-empty string');
  }
}

function assertValidRotation(rotation: RotationQuarter): void {
  if (!(ROTATION_QUARTERS as readonly number[]).includes(rotation)) {
    throw new Error(`Rotation must be 0/90/180/270, got ${rotation}`);
  }
}

export function legacySlotCenterAnchor(slot: Pick<SlotDef, 'col' | 'row'>): SubgridCoord {
  return mapLegacyCenter(slot.col, slot.row, FOUNDATION_SPEC).nearestSubcell;
}

export function centerAnchorForSlotId(slotId: string): SubgridCoord {
  const slot = slotById(slotId);
  if (!slot) {
    throw new Error(`Unknown legacy slot ${slotId}`);
  }
  return legacySlotCenterAnchor(slot);
}

export function currentLegacyWorldPosition(slot: Pick<SlotDef, 'col' | 'row'>): WorldPoint {
  return legacyGridToWorld(slot.col, slot.row);
}

export function createCandidatePlacement(input: {
  id: string;
  kind: EntityKind;
  anchor: SubgridCoord;
  rotation?: RotationQuarter;
  spec?: FootprintSpec;
  originSlotId?: string | null;
}): CandidatePlacement {
  assertNonEmptyId(input.id);
  const rotation = input.rotation ?? 0;
  assertValidRotation(rotation);
  return {
    id: input.id,
    kind: input.kind,
    originSlotId: input.originSlotId ?? null,
    anchor: { sx: input.anchor.sx, sz: input.anchor.sz },
    rotation,
    spec: input.spec ?? visualFootprintRecommendation(input.kind).spec,
  };
}

/** Map a current slotId-placed entity onto the exact 5x5 center subcell. */
export function migrateLegacyEntity(entity: Pick<PlacedEntity, 'id' | 'kind' | 'slotId'>): CandidatePlacement {
  const slot = slotById(entity.slotId);
  if (!slot) {
    throw new Error(`Unknown legacy slot ${entity.slotId}`);
  }
  return createCandidatePlacement({
    id: entity.id,
    kind: entity.kind,
    originSlotId: entity.slotId,
    anchor: legacySlotCenterAnchor(slot),
    rotation: 0,
  });
}

export function candidateWorldPosition(placement: CandidatePlacement): WorldPoint {
  const slotId = slotIdForCenterAnchor(placement.anchor);
  if (slotId) {
    const slot = slotById(slotId);
    if (slot) {
      return currentLegacyWorldPosition(slot);
    }
  }
  return subcellWorldCenter(placement.anchor, FOUNDATION_SPEC);
}

export function candidateOccupiedSubcells(placement: CandidatePlacement): SubgridCoord[] {
  return occupiedSubcells(placement.anchor, placement.spec, placement.rotation);
}

export function candidatePlacementsOverlap(a: CandidatePlacement, b: CandidatePlacement): boolean {
  return occupiedSetsOverlap(candidateOccupiedSubcells(a), candidateOccupiedSubcells(b));
}

export function evaluateCoOccupancy(
  a: CandidatePlacement,
  b: CandidatePlacement,
): { ok: true } | { ok: false; reason: 'overlap' } {
  return candidatePlacementsOverlap(a, b) ? { ok: false, reason: 'overlap' } : { ok: true };
}

export function slotIdForCenterAnchor(anchor: SubgridCoord): string | undefined {
  return SLOT_CENTER_BY_KEY.get(subcellKey(anchor));
}

/**
 * Current saves can only store one entity per known slotId.
 * Center-anchored migrated entities resolve back to that slot.
 * Any other subcell anchor is future-only / non-legacy-representable.
 */
export function legacyRepresentability(placement: CandidatePlacement): LegacyCompatibility {
  const slotId = slotIdForCenterAnchor(placement.anchor);
  if (!slotId) {
    return { kind: 'future_only', reason: 'non_center_anchor' };
  }
  return { kind: 'legacy_representable', slotId };
}

export function allLegacySlotCenterAnchors(): Array<{ slotId: string; anchor: SubgridCoord; world: WorldPoint }> {
  return SLOTS.map((slot) => ({
    slotId: slot.id,
    anchor: legacySlotCenterAnchor(slot),
    world: currentLegacyWorldPosition(slot),
  }));
}

export const PLACEMENT_PROOF_CONSTANTS = {
  division: PLACEMENT_DIVISION,
  cellSize: PLACEMENT_CELL_SIZE,
  footprintDivision: FOOTPRINT_DIVISION,
  footprintCellSize: FOOTPRINT_CELL_SIZE,
} as const;
